import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import type { ComponentProps } from 'react';
import { mergeStyling } from '../common-functions';

const Popover = ({ ...props }: PopoverPrimitive.Root.Props) => {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
};

const PopoverTrigger = ({ className, ...props }: PopoverPrimitive.Trigger.Props) => {
  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      className={mergeStyling('outline-hidden focus-visible:outline-2 focus-visible:outline-primary-1', className)}
      {...props}
    />
  );
};

const PopoverClose = ({ ...props }: PopoverPrimitive.Close.Props) => {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />;
};

type PopoverAlign = PopoverPrimitive.Positioner.Props['align'];
type PopoverSide = PopoverPrimitive.Positioner.Props['side'];

const PopoverContent = ({
  className,
  align = 'center',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  collisionAvoidance,
  anchor,
  container,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset' | 'collisionAvoidance' | 'anchor'
  > &
  Pick<PopoverPrimitive.Portal.Props, 'container'>) => {
  return (
    <PopoverPrimitive.Portal container={container}>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        collisionAvoidance={collisionAvoidance}
        anchor={anchor}
        className="isolate z-50"
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={mergeStyling(
            'z-50 flex w-72 flex-col gap-2.5 rounded-sm bg-mono-1 p-2.5 text-mono-0 text-sm shadow-md outline-hidden ring-1 ring-gray-4 duration-100',
            className
          )}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
};

const PopoverHeader = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div data-slot="popover-header" className={mergeStyling('flex flex-col gap-1 text-sm', className)} {...props} />
  );
};

const PopoverTitle = ({ className, ...props }: PopoverPrimitive.Title.Props) => {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      className={mergeStyling('font-medium text-base', className)}
      {...props}
    />
  );
};

const PopoverDescription = ({ className, ...props }: PopoverPrimitive.Description.Props) => {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={mergeStyling('text-gray-2', className)}
      {...props}
    />
  );
};

export {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  type PopoverAlign,
  type PopoverSide,
};
